import logger from '../utils/logger';

/**
 * HITL-1492 Pillar 3 (Part A / Capture) — the issue-report taxonomy.
 *
 * The category is NOT emitted on the wire: the consumer owns the type -> category
 * mapping and re-derives it. It lives here because the two surfaces that render the
 * taxonomy GROUP by it — HITL-1500's "More" list and HITL-1501's type selector — and
 * a flat 19-item list is exactly what buried `Unnecessary Staff Intervention` (last
 * in the Slack form's scrolling dropdown, and ~52% of all reports).
 *
 * Declaration order is load-bearing: `groupIssueTypesByCategory` renders groups in
 * this order, so `other` sits after `storeBehavior` to put "Other" last.
 */
export enum IssueCategory {
  aiEscalation = 'ai_escalation',
  aiOrder = 'ai_order',
  aiResponse = 'ai_response',
  asr = 'asr',
  audio = 'audio',
  pos = 'pos',
  menuConfig = 'menu_config',
  storeBehavior = 'store_behavior',
  other = 'other',
  unclassified = 'unclassified',
}

export interface IssueTypeDefinition {
  /**
   * Verbatim Slack-form value. Byte-exact on purpose: it is the join key for every
   * historical report, and HITL-1502's Slack mirror must reproduce it unchanged.
   */
  value: string;
  category: IssueCategory;
}

/**
 * Internal 19th value — the Slack form has no equivalent. A report is created
 * `Unclassified` on press and stays that way until the agent classifies it, so
 * this is the absence of a choice rather than a choice: it is never offered as a
 * quick-pick candidate.
 */
export const UNCLASSIFIED_ISSUE_TYPE = 'Unclassified';

/**
 * The explicit escape hatch, and NOT a Slack-form value — the form has no
 * equivalent, so HITL-1502 maps it per workflow like any other type.
 *
 * Distinct from `Unclassified` on purpose. `Unclassified` means "nobody has triaged
 * this yet"; `Other` means "an agent looked and none of the 18 fit". Collapsing them
 * loses the difference between an untriaged report and a taxonomy gap — and the
 * alternative an agent reaches for under time pressure is picking the closest WRONG
 * value, which poisons the distribution undetectably.
 *
 * Two rules ride on this constant:
 * - HITL-1500 must EXCLUDE it from ranker candidates (it is unpredictable by
 *   definition, so a top chip offering it would be actively harmful) — "More" only.
 * - HITL-1501 must REQUIRE `issue_detail` when it is selected. `Other` with an empty
 *   detail says LESS than `Unclassified`: it destroys the untriaged signal and adds
 *   nothing.
 */
export const OTHER_ISSUE_TYPE = 'Other';

/**
 * The 18 Slack-form values IN FORM ORDER — not alphabetical, not by frequency —
 * plus `Other` last. Order is load-bearing: HITL-1500's "More" list and HITL-1501's
 * classification panel both render from it.
 *
 * Only the first 18 are byte-exact Slack-form values; `Other` is ours.
 */
export const ISSUE_TYPES: IssueTypeDefinition[] = [
  { value: 'AI - Escalation', category: IssueCategory.aiEscalation },
  { value: 'AI - Missing Item', category: IssueCategory.aiOrder },
  { value: 'AI - Incorrect Item', category: IssueCategory.aiOrder },
  { value: 'AI - Incorrect Modification', category: IssueCategory.aiOrder },
  { value: 'AI - Missing Modification', category: IssueCategory.aiOrder },
  { value: 'AI - Upsell', category: IssueCategory.aiOrder },
  {
    value: 'AI - Response - Missing Response',
    category: IssueCategory.aiResponse,
  },
  {
    value: 'AI - Response - Incorrect Response',
    category: IssueCategory.aiResponse,
  },
  { value: 'AI - Response - No Response', category: IssueCategory.aiResponse },
  {
    value: 'AI - Response - Slow Response',
    category: IssueCategory.aiResponse,
  },
  { value: 'AI - Incorrect Price', category: IssueCategory.aiOrder },
  { value: 'ASR - Missing Transcription', category: IssueCategory.asr },
  { value: 'ASR - Incorrect Transcription', category: IssueCategory.asr },
  { value: 'Audio - Low Volume', category: IssueCategory.audio },
  { value: 'Audio - Choppy', category: IssueCategory.audio },
  { value: 'POS - Validation', category: IssueCategory.pos },
  // The only value with no family prefix, and its meaning is ambiguous (AI failed
  // to auto-combo, or wrongly auto-combo'd?). `menu_config` is a considered guess;
  // the Unleash variant can override the category without a release, which is why
  // the open question does not gate this ticket.
  { value: 'Autocombo', category: IssueCategory.menuConfig },
  {
    value: 'Unnecessary Staff Intervention',
    category: IssueCategory.storeBehavior,
  },
  { value: OTHER_ISSUE_TYPE, category: IssueCategory.other },
];

const KNOWN_CATEGORIES: string[] = Object.values(IssueCategory);

const isIssueTypeDefinition = (entry: any): entry is IssueTypeDefinition =>
  Boolean(
    entry &&
      typeof entry === 'object' &&
      typeof entry.value === 'string' &&
      entry.value.trim() &&
      typeof entry.category === 'string' &&
      KNOWN_CATEGORIES.includes(entry.category)
  );

export interface ParsedIssueReportingVariant {
  /** Validated override, or null to keep the compiled-in ISSUE_TYPES. */
  types: IssueTypeDefinition[] | null;
  /** Every other key, passed through untouched for HITL-1499/1500 to read. */
  rest: Record<string, unknown>;
}

const EMPTY_VARIANT: ParsedIssueReportingVariant = { types: null, rest: {} };

/**
 * Parse the `hitl_issue_reporting` Unleash variant payload.
 *
 * The payload is an OBJECT, not a bare array: HITL-1500 adds its own keys
 * (quickpick_digits, quickpick_ms, chip_count, brand_priors) to the same variant,
 * and a bare array would force a payload-shape migration mid-pilot on the one
 * control surface whose purpose is changing without a release. A bare array is
 * still tolerated and read as `types`, since that is the intuitive thing to paste.
 *
 * `types` validation is all-or-nothing so the taxonomy is never half-applied — but
 * a bad `types` must NOT discard `rest`, or a typo in the taxonomy would also
 * silently switch off HITL-1500's digit binding.
 */
export const parseIssueReportingVariant = (
  raw: string | undefined | null
): ParsedIssueReportingVariant => {
  if (!raw) {
    return EMPTY_VARIANT;
  }

  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    logger.warn({
      message:
        'Ignoring hitl_issue_reporting variant payload: not valid JSON. Using the compiled-in issue types.',
    });
    return EMPTY_VARIANT;
  }

  const { types: rawTypes, rest } = Array.isArray(parsed)
    ? { types: parsed as unknown, rest: {} as Record<string, unknown> }
    : (() => {
        const { types, ...others } = (parsed || {}) as Record<string, unknown>;
        return { types, rest: others };
      })();

  if (rawTypes === undefined) {
    return { types: null, rest };
  }

  if (!Array.isArray(rawTypes) || !rawTypes.length) {
    logger.warn({
      message:
        'Ignoring hitl_issue_reporting `types` override: not a non-empty array. Using the compiled-in issue types.',
    });
    return { types: null, rest };
  }

  const invalid = rawTypes.find((entry) => !isIssueTypeDefinition(entry));
  if (invalid) {
    logger.warn({
      message:
        'Ignoring hitl_issue_reporting `types` override: an entry is missing a non-empty `value` or carries an unknown `category`. Using the compiled-in issue types.',
      invalidEntry: JSON.stringify(invalid),
    });
    return { types: null, rest };
  }

  return {
    types: rawTypes.map(({ value, category }) => ({ value, category })),
    rest,
  };
};

/**
 * Flat `issue_type -> issue_category` map over the ACTIVE list, so an override
 * remaps too.
 */
export const buildIssueCategoryMap = (
  types: IssueTypeDefinition[] = ISSUE_TYPES
): Record<string, IssueCategory> =>
  types.reduce((acc, { value, category }) => {
    acc[value] = category;
    return acc;
  }, {} as Record<string, IssueCategory>);

/**
 * Grouped by category, for HITL-1500's "More" affordance and HITL-1501's
 * classification list. A flat 19-item list is exactly what buried
 * `Unnecessary Staff Intervention` (last in the form's scrolling dropdown, and
 * 52% of all reports), so those surfaces render this — not the flat list.
 *
 * Category order follows IssueCategory declaration order; within a category,
 * form order. Empty categories are omitted, and `unclassified` never appears
 * because it is not a real choice. `other` DOES appear — it is a real choice, and
 * its declaration position puts it last.
 */
export const groupIssueTypesByCategory = (
  types: IssueTypeDefinition[] = ISSUE_TYPES
): Array<{ category: IssueCategory; types: IssueTypeDefinition[] }> =>
  (Object.values(IssueCategory) as IssueCategory[])
    .filter((category) => category !== IssueCategory.unclassified)
    .map((category) => ({
      category,
      types: types.filter((type) => type.category === category),
    }))
    .filter((group) => group.types.length > 0);
